import { useState, useEffect } from 'react';
import { serverTypeShort } from '@/utils/server-type';
import { useTranslation } from 'react-i18next';
import {
  RefreshCw, Download, Trash2, ChevronLeft, ChevronRight,
  Play, Square, Loader2, RotateCcw, Clock, Radio, CheckCircle,
  AlertCircle, ChevronDown, ChevronUp, Hash, Users, LayoutGrid, Euro,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { partialBadgeText } from '@/utils/partial-start';
import { scheduleService, Schedule as ScheduleType, getErrorMessage } from '@/services';
import dayjs from 'dayjs';

export default function Schedule() {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [schedules, setSchedules] = useState<ScheduleType[]>([]);
  const [grouped, setGrouped] = useState<{
    scheduled: ScheduleType[];
    live: ScheduleType[];
    completed: ScheduleType[];
    error: ScheduleType[];
  }>({ scheduled: [], live: [], completed: [], error: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [collapsedComps, setCollapsedComps] = useState<Set<string>>(new Set());
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchSchedules = async () => {
    setIsLoading(true);
    try {
      const response = await scheduleService.getByDate(selectedDate);
      setSchedules(response.schedules);
      setGrouped(response.grouped);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const silentRefresh = async () => {
    try {
      const response = await scheduleService.getByDate(selectedDate);
      setSchedules(response.schedules);
      setGrouped(response.grouped);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, [selectedDate]);

  const goToPreviousDay = () => {
    setSelectedDate(dayjs(selectedDate).subtract(1, 'day').format('YYYY-MM-DD'));
  };

  const goToNextDay = () => {
    setSelectedDate(dayjs(selectedDate).add(1, 'day').format('YYYY-MM-DD'));
  };

  const goToToday = () => {
    setSelectedDate(dayjs().format('YYYY-MM-DD'));
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      await scheduleService.generateFromEpg(selectedDate);
      toast.success(t('schedule.generated'));
      silentRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClear = async () => {
    setIsClearing(true);
    try {
      await scheduleService.clearByDate(selectedDate);
      toast.success(t('schedule.cleared'));
      silentRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsClearing(false);
    }
  };

  const handleDownload = async () => {
    try {
      const blob = await scheduleService.downloadSchedule(selectedDate);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `schedule_${selectedDate}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleStartStream = async (assetId: string) => {
    setActionLoading(assetId);
    try {
      await scheduleService.startEvent(assetId);
      toast.success(t('schedule.startEvent'));
      silentRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleStopStream = async (assetId: string) => {
    setActionLoading(assetId);
    try {
      await scheduleService.stopEvent(assetId);
      toast.success(t('schedule.stopEvent'));
      silentRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetEvent = async (assetId: string) => {
    setActionLoading(assetId);
    try {
      await scheduleService.resetEvent(assetId);
      toast.success(t('schedule.resetEvent'));
      silentRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setActionLoading(null);
    }
  };

  const toggleCompetition = (comp: string) => {
    setCollapsedComps(prev => {
      const next = new Set(prev);
      if (next.has(comp)) {
        next.delete(comp);
      } else {
        next.add(comp);
      }
      return next;
    });
  };

  // Group schedules by competition_name
  const groupedByCompetition: Record<string, ScheduleType[]> = {};
  for (const s of schedules) {
    const comp = s.competition_name || t('schedule.other');
    if (!groupedByCompetition[comp]) {
      groupedByCompetition[comp] = [];
    }
    groupedByCompetition[comp].push(s);
  }

  const getStatusBadge = (status: number, partial?: ScheduleType['partial_start']) => {
    // Status 0 after a partial start (some legs live, the retry starts the rest): amber "Parziale n/m" instead of "Programmato"
    if (status === 0 && partial) {
      return { icon: <Radio size={14} />, label: `${t('schedule.statusPartial')} ${partialBadgeText(partial)}`, className: 'bg-amber-100 text-amber-700' };
    }
    switch (status) {
      case 0: return { icon: <Clock size={14} />, label: t('schedule.statusScheduled'), className: 'bg-primary-50 text-primary-600' };
      case 1: return { icon: <Radio size={14} className="animate-pulse" />, label: t('schedule.statusLive'), className: 'bg-success-50 text-success-600' };
      case 2: return { icon: <CheckCircle size={14} />, label: t('schedule.statusEnded'), className: 'bg-dark-800 text-dark-400' };
      case 3: return { icon: <AlertCircle size={14} />, label: t('schedule.statusError'), className: 'bg-error-50 text-error-600' };
      default: return { icon: null, label: t('schedule.unknown'), className: 'bg-dark-800 text-dark-400' };
    }
  };

  const getChannelModeBadge = (mode?: string) => {
    switch (mode) {
      case 'numbered':
        return { icon: <Hash size={12} />, label: t('schedule.channelModeNumbered'), className: 'bg-primary-50 text-primary-600 border border-primary-200' };
      case 'team':
        return { icon: <Users size={12} />, label: t('schedule.channelModeTeam'), className: 'bg-success-50 text-success-600 border border-success-100' };
      case 'both':
        return { icon: <LayoutGrid size={12} />, label: t('schedule.channelModeBoth'), className: 'bg-violet-50 text-violet-600 border border-violet-200' };
      default:
        return null;
    }
  };

  const formattedDate = dayjs(selectedDate).format('dddd, D MMMM YYYY');

  function getTeamChannelNames(schedule: ScheduleType): { homeChannel: string; awayChannel: string } | null {
    const ch = schedule.xaccel_channel_name;
    if (!ch) return null;
    const home = schedule.home_team;
    const away = schedule.away_team;
    if (!home && !away) return null;

    // Extract prefix by removing trailing _XX (slot number)
    const prefixMatch = ch.replace(/_\d{2}$/, '');
    if (prefixMatch === ch && !/\d{2}$/.test(ch)) return null;

    const sanitize = (name: string) =>
      name.replace(/[^a-zA-Z0-9\s_-]/g, '').replace(/\s+/g, '_').substring(0, 30);

    return {
      homeChannel: home ? `${prefixMatch}_|_${sanitize(home)}` : '',
      awayChannel: away ? `${prefixMatch}_|_${sanitize(away)}` : '',
    };
  }

  function renderChannelInfo(schedule: ScheduleType) {
    const mode = schedule.channel_mode;
    const ch = schedule.xaccel_channel_name;
    const home = schedule.home_team;
    const away = schedule.away_team;

    if ((mode === 'team' || mode === 'both') && (home || away)) {
      const teamChannels = getTeamChannelNames(schedule);
      return (
        <div>
          <span className="text-dark-300 text-sm tracking-wide block">{ch || '-'}</span>
          {teamChannels ? (
            <div className="mt-0.5">
              {teamChannels.homeChannel && (
                <span className="font-mono text-[11px] text-dark-500 block">{teamChannels.homeChannel}</span>
              )}
              {teamChannels.awayChannel && (
                <span className="font-mono text-[11px] text-dark-500 block">{teamChannels.awayChannel}</span>
              )}
            </div>
          ) : (
            (home || away) && (
              <span className="text-dark-500 text-[11px] mt-0.5 block">
                {[home, away].filter(Boolean).join(' vs ')}
              </span>
            )
          )}
        </div>
      );
    }

    return (
      <span className="text-dark-300 text-sm tracking-wide">{ch || '-'}</span>
    );
  }

  /** Badge "a pagamento": rosso PPV (acquisto singolo) o giallo Extra (pacchetto/addon).
   *  Tooltip con gli EntitlementIds DAZN grezzi per diagnosi. */
  function renderPaywallBadge(schedule: ScheduleType) {
    if (schedule.paywall !== 'ppv' && schedule.paywall !== 'extra') return null;
    const isPpv = schedule.paywall === 'ppv';
    const hint = isPpv ? t('schedule.paywallPpvHint') : t('schedule.paywallExtraHint');
    const ids = schedule.paywall_ids?.length ? `\n${schedule.paywall_ids.join(', ')}` : '';
    return (
      <span
        className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide shrink-0 ${
          isPpv
            ? 'bg-error-50 text-error-600 border border-error-100'
            : 'bg-warning-50 text-warning-600 border border-warning-100'
        }`}
        title={`${hint}${ids}`}
      >
        <Euro size={10} />
        {isPpv ? t('schedule.paywallPpv') : t('schedule.paywallExtra')}
      </span>
    );
  }

  /** Badge stato + badge per-server. Contenuto puro: il wrapper flex lo mette il chiamante
   *  (tabella desktop e card mobile lo riusano identico). */
  function renderStatusBadges(schedule: ScheduleType) {
    const statusInfo = getStatusBadge(schedule.status, schedule.partial_start);
    return (
      <>
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusInfo.className}`}
          title={schedule.status === 3 ? schedule.status_message || '' : ''}
        >
          {statusInfo.icon}
          {statusInfo.label}
        </span>
        {schedule.status === 1 && schedule.servers && schedule.servers.length > 0 ? (
          schedule.servers.map(srv => (
            <span
              key={srv.id}
              className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                srv.status === 'running'
                  ? srv.server_type === 'ffstream' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                  : srv.status === 'unknown' ? 'bg-gray-100 text-gray-500'
                  : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
              }`}
              title={`${srv.name}: ${srv.status}`}
            >
              {serverTypeShort(srv.server_type)} {srv.status === 'running' ? '✓' : srv.status === 'unknown' ? '?' : '⚠'}
            </span>
          ))
        ) : (
          <>
            {schedule.status === 1 && schedule.xaccel_real_status === 'running' && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700" title={t('schedule.serverRunningTooltip', { server: serverTypeShort(schedule.server_type) })}>{serverTypeShort(schedule.server_type)} ✓</span>
            )}
            {schedule.status === 1 && schedule.xaccel_real_status && schedule.xaccel_real_status !== 'running' && schedule.xaccel_real_status !== 'unknown' && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-yellow-50 text-yellow-700 border border-yellow-200" title={`${serverTypeShort(schedule.server_type)}: ${schedule.xaccel_real_status}`}>⚠</span>
            )}
            {schedule.status === 1 && schedule.xaccel_real_status === 'unknown' && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500" title={t('schedule.serverStatusUnknown', { server: serverTypeShort(schedule.server_type) })}>?</span>
            )}
          </>
        )}
      </>
    );
  }

  /** Azioni evento (Play/Stop/Reset) per stato. Contenuto puro: wrapper flex dal chiamante. */
  function renderEventActions(schedule: ScheduleType) {
    if (actionLoading === schedule.dazn_asset_id) {
      return <Loader2 size={16} className="animate-spin text-dark-500" />;
    }
    return (
      <>
        {schedule.status === 0 && (
          <>
            <button
              onClick={() => handleStartStream(schedule.dazn_asset_id)}
              className="p-1.5 rounded-lg hover:bg-success-50 text-dark-400 hover:text-success-600 transition-colors"
              title={t('schedule.startEvent')}
            >
              <Play size={16} />
            </button>
            <button
              onClick={() => handleResetEvent(schedule.dazn_asset_id)}
              className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-500 hover:text-dark-300 transition-colors"
              title={t('schedule.resetEvent')}
            >
              <RotateCcw size={14} />
            </button>
          </>
        )}
        {schedule.status === 1 && (
          <button
            onClick={() => handleStopStream(schedule.dazn_asset_id)}
            className="p-1.5 rounded-lg hover:bg-error-50 text-dark-400 hover:text-error-600 transition-colors"
            title={t('schedule.stopEvent')}
          >
            <Square size={16} />
          </button>
        )}
        {schedule.status === 2 && (
          <button
            onClick={() => handleResetEvent(schedule.dazn_asset_id)}
            className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-500 hover:text-dark-300 transition-colors"
            title={t('schedule.resetEvent')}
          >
            <RotateCcw size={14} />
          </button>
        )}
        {schedule.status === 3 && (
          <button
            onClick={() => handleResetEvent(schedule.dazn_asset_id)}
            className="p-1.5 rounded-lg hover:bg-warning-50 text-dark-400 hover:text-warning-600 transition-colors"
            title={schedule.status_message || t('schedule.resetEvent')}
          >
            <RotateCcw size={14} />
          </button>
        )}
        {schedule.status === 4 && (
          <button
            onClick={() => handleResetEvent(schedule.dazn_asset_id)}
            className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-500 hover:text-dark-300 transition-colors"
            title={t('schedule.resetEvent')}
          >
            <RotateCcw size={14} />
          </button>
        )}
      </>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('schedule.title')}</h1>
          <p className="text-dark-400 mt-1 capitalize">{formattedDate}</p>
        </div>
        <div className="flex flex-wrap gap-2 md:gap-3">
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="btn-secondary"
          >
            {isGenerating ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
            {t('schedule.generateSchedule')}
          </button>
          <button onClick={handleDownload} className="btn-secondary">
            <Download size={18} />
            {t('schedule.downloadSchedule')}
          </button>
          <button
            onClick={handleClear}
            disabled={isClearing}
            className="btn-danger"
          >
            {isClearing ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
            {t('schedule.clearSchedule')}
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="card !py-4">
          <p className="text-dark-400 text-sm">{t('schedule.total')}</p>
          <p className="text-2xl font-bold text-dark-100">{schedules.length}</p>
        </div>
        <div className="card !py-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-success-500 animate-pulse" />
            <p className="text-dark-400 text-sm">{t('schedule.statusLive')}</p>
          </div>
          <p className="text-2xl font-bold text-success-600">{grouped.live.length}</p>
        </div>
        <div className="card !py-4">
          <p className="text-dark-400 text-sm">{t('schedule.statusScheduled')}</p>
          <p className="text-2xl font-bold text-primary-600">{grouped.scheduled.length}</p>
        </div>
        <div className="card !py-4">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-dark-400 text-sm">{t('schedule.statusEnded')}</p>
              <p className="text-xl font-bold text-dark-400">{grouped.completed.length}</p>
            </div>
            {grouped.error.length > 0 && (
              <div>
                <p className="text-dark-400 text-sm">{t('schedule.statusError')}</p>
                <p className="text-xl font-bold text-error-600">{grouped.error.length}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Date Navigation */}
      <div className="flex items-center justify-center gap-4">
        <button onClick={goToPreviousDay} className="btn-ghost">
          <ChevronLeft size={20} />
        </button>
        <div className="flex items-center gap-3">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="input w-auto"
          />
          <button onClick={goToToday} className="btn-secondary btn-sm">
            {t('schedule.today')}
          </button>
        </div>
        <button onClick={goToNextDay} className="btn-ghost">
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Events grouped by competition */}
      {isLoading ? (
        <div className="card">
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-dark-700 rounded" />
            ))}
          </div>
        </div>
      ) : schedules.length === 0 ? (
        <div className="card">
          <p className="text-dark-400">{t('common.noData')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedByCompetition).map(([comp, events]) => {
            const isCollapsed = collapsedComps.has(comp);
            const channelMode = events[0]?.channel_mode;
            const channelModeBadge = getChannelModeBadge(channelMode);

            return (
              <div key={comp} className="card !p-0 overflow-hidden">
                {/* Competition header */}
                <button
                  onClick={() => toggleCompetition(comp)}
                  className="w-full flex items-center justify-between px-5 py-3 bg-dark-800 hover:bg-dark-800 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-dark-100">{comp}</span>
                    <span className="px-2 py-0.5 rounded-full bg-dark-700 text-dark-400 text-xs font-medium">
                      {events.length}
                    </span>
                    {channelModeBadge && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${channelModeBadge.className}`}>
                        {channelModeBadge.icon}
                        {channelModeBadge.label}
                      </span>
                    )}
                  </div>
                  {isCollapsed ? <ChevronDown size={16} className="text-dark-500" /> : <ChevronUp size={16} className="text-dark-500" />}
                </button>

                {/* Events table (desktop, invariata da md in su) */}
                {!isCollapsed && (
                  <table className="w-full table-fixed hidden md:table">
                    <colgroup>
                      <col style={{ width: 72 }} />
                      <col />
                      <col style={{ width: 280 }} />
                      <col style={{ width: 160 }} />
                      <col style={{ width: 80 }} />
                    </colgroup>
                    <tbody className="divide-y divide-dark-700">
                      {events.map((schedule) => {
                        return (
                          <tr key={schedule.id} className="hover:bg-dark-800 transition-colors">
                            {/* Time */}
                            <td className="px-4 py-3">
                              <span className="text-dark-300 text-sm tracking-wide">
                                {dayjs(schedule.event_start_time).format('HH:mm')}
                              </span>
                            </td>

                            {/* Event */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 min-w-0">
                                <p className="text-sm text-dark-100 truncate tracking-wide">{schedule.event_title}</p>
                                {renderPaywallBadge(schedule)}
                              </div>
                              {(schedule.home_team || schedule.away_team) && (
                                <p className="text-[11px] text-dark-500 mt-0.5 truncate">
                                  {schedule.home_team} vs {schedule.away_team}
                                </p>
                              )}
                            </td>

                            {/* Channel */}
                            <td className="px-4 py-3">
                              {renderChannelInfo(schedule)}
                            </td>

                            {/* Status */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1.5">
                                {renderStatusBadges(schedule)}
                              </div>
                            </td>

                            {/* Actions */}
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {renderEventActions(schedule)}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {/* Events cards (mobile: orario+titolo, azioni a destra, stato e canale sotto) */}
                {!isCollapsed && (
                  <div className="md:hidden divide-y divide-dark-700">
                    {events.map((schedule) => (
                      <div key={schedule.id} className="px-4 py-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm text-dark-100 tracking-wide">
                              <span className="text-dark-400 mr-2">{dayjs(schedule.event_start_time).format('HH:mm')}</span>
                              {schedule.event_title}
                            </p>
                            {(schedule.home_team || schedule.away_team) && (
                              <p className="text-[11px] text-dark-500 mt-0.5">
                                {schedule.home_team} vs {schedule.away_team}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {renderEventActions(schedule)}
                          </div>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          {renderStatusBadges(schedule)}
                          {renderPaywallBadge(schedule)}
                        </div>
                        <div className="mt-1.5">
                          {renderChannelInfo(schedule)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
